import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search,
  Filter,
  RefreshCw,
  X,
  Briefcase,
  ChevronDown,
  CheckCircle,
  MapPin,
  Building2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Eye,
  RotateCcw,
  Wifi,
  FileText,
  Award,
} from "lucide-react";

/* ─── Animated Counter ─────────────────────────────────────────────── */
function AnimatedNumber({ value, duration = 1100 }) {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const step = (ts) => {
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setDisplay(Math.round(value * ease));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);
  return <span>{display}</span>;
}

/* ─── KPI Config ───────────────────────────────────────────────────── */
const KPI_CONFIG = {
  Active: {
    gradient: "linear-gradient(135deg,#1e40af 0%,#3b82f6 50%,#60a5fa 100%)",
    softBg: "#eff6ff",
    border: "#bfdbfe",
    iconBg: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
    textAccent: "#1d4ed8",
    barColor: "#3b82f6",
    barTrack: "#bfdbfe",
    shadow: "0 8px 32px -4px #3b82f640",
    Icon: CheckCircle,
  },
  Open: {
    gradient: "linear-gradient(135deg,#065f46 0%,#059669 50%,#34d399 100%)",
    softBg: "#f0fdf4",
    border: "#bbf7d0",
    iconBg: "linear-gradient(135deg,#047857,#10b981)",
    textAccent: "#047857",
    barColor: "#10b981",
    barTrack: "#bbf7d0",
    shadow: "0 8px 32px -4px #10b98140",
    Icon: Briefcase,
  },
  "Re-Open": {
    gradient: "linear-gradient(135deg,#92400e 0%,#d97706 50%,#fbbf24 100%)",
    softBg: "#fffbeb",
    border: "#fde68a",
    iconBg: "linear-gradient(135deg,#b45309,#f59e0b)",
    textAccent: "#b45309",
    barColor: "#f59e0b",
    barTrack: "#fde68a",
    shadow: "0 8px 32px -4px #f59e0b40",
    Icon: RotateCcw,
  },
};

const DEFAULT_JOB_STATUSES = ["Active", "Open", "Re-Open"];

/* ─── KPI Card ─────────────────────────────────────────────────────── */
function KpiCard({ status, total, addressed, notAddressed, delay }) {
  const cfg = KPI_CONFIG[status] || KPI_CONFIG["Active"];
  const Icon = cfg.Icon;

  return (
    <div
      style={{
        animation: `kpiSlide 0.65s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms both`,
        boxShadow: cfg.shadow,
        background: cfg.softBg,
        border: `1.5px solid ${cfg.border}`,
      }}
      className="w-full md:max-w-[320px] rounded-2xl overflow-hidden"
    >
      <div style={{ background: cfg.gradient, height: 4 }} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            style={{
              background: cfg.iconBg,
              boxShadow: `0 4px 14px ${cfg.barColor}60`,
            }}
          >
            <Icon className="w-4.5 h-4.5 text-white" />
          </div>
          <div className="text-right">
            <div
              className="text-2xl font-black"
              style={{ color: cfg.textAccent }}
            >
              <AnimatedNumber value={total} />
            </div>
            <div
              className="text-[11px] font-bold uppercase tracking-widest mt-0.5"
              style={{ color: cfg.textAccent, opacity: 0.6 }}
            >
              {status}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[
            ["✓ Addressed", addressed, cfg.textAccent],
            ["◷ Not Addressed", notAddressed, "#64748b"],
          ].map(([label, val, color]) => (
            <div
              key={label}
              className="bg-white rounded-xl p-2.5 border border-white/80 shadow-sm"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                {label}
              </div>
              <div className="text-base font-black" style={{ color }}>
                <AnimatedNumber value={val} duration={1300} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Tag config ───────────────────────────────────────────────────── */
const statusAccent = {
  Active: { line: "#3b82f6", glow: "#eff6ff" },
  Open: { line: "#10b981", glow: "#f0fdf4" },
  "Re-Open": { line: "#f59e0b", glow: "#fffbeb" },
};

const ADDRESSED_BUTTON_SOLID = "#10b981";
const ADDRESSED_BUTTON_GRADIENT = "linear-gradient(135deg,#059669,#10b981)";
const ADDRESSED_BUTTON_SHADOW = "0 4px 14px #10b98150";

/* ─── Info Cell ────────────────────────────────────────────────────── */

/* ─── Job Card ─────────────────────────────────────────────────────── */
function JobCard({
  req,
  selectionMode,
  isSelected,
  onToggle,
  onAddressed,
  delay,
}) {
  const [hovered, setHovered] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [copiedDescription, setCopiedDescription] = useState(false);
  const descriptionContentRef = useRef(null);
  const accent = statusAccent[req.job_status] || statusAccent.Active;
  const descriptionHtml = String(req.job_description_html || "").replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    "",
  );
  const hasDescription = Boolean(descriptionHtml.trim());
  const isAddressed = req.addressed_status === "addressed";
  const currentUserAddressedFlag =
    String(req.current_user_addressed_flag || "0").trim() === "1";
  const addressedByNames = String(req.addressed_by_names || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
  const addressedByDisplay = addressedByNames.join(", ");
  const jobStatus = String(req.job_status || "").trim();
  const jobStatusChipClass =
    jobStatus === "Open"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : jobStatus === "Re-Open"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-blue-200 bg-blue-50 text-blue-700";

  const copyDescriptionToClipboard = async () => {
    try {
      const plainText =
        descriptionContentRef.current?.innerText?.trim() || descriptionHtml;
      if (navigator.clipboard?.write && window.ClipboardItem) {
        const item = new window.ClipboardItem({
          "text/plain": new Blob([plainText], { type: "text/plain" }),
          "text/html": new Blob([descriptionHtml], { type: "text/html" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setCopiedDescription(true);
      window.setTimeout(() => setCopiedDescription(false), 1500);
    } catch (error) {
      // Ignore clipboard errors silently.
    }
  };

  return (
    <>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          animation: `cardFadeIn 0.45s ease ${delay}ms both`,
          transform: hovered ? "translateY(-3px)" : "translateY(0)",
          transition:
            "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s",
          boxShadow: hovered
            ? `0 16px 48px -8px #00000018, 0 4px 16px -4px ${accent.line}25`
            : "0 2px 12px -4px #0000000e",
          border: isSelected
            ? `1.5px solid ${accent.line}`
            : "1.5px solid #e2e8f0",
          background: isSelected ? accent.glow : "#ffffff",
        }}
        className="w-full max-w-6xl mx-auto rounded-2xl overflow-hidden min-h-[145px]"
      >
        <div className="flex items-start">
          <div
            className="w-1 shrink-0 rounded-l-2xl"
            style={{ background: accent.line }}
          />
          {selectionMode && (
            <div className="shrink-0 pt-4 pl-3">
              <button
                onClick={() => onToggle(req)}
                className="w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all"
                style={{
                  background: isSelected ? accent.line : "white",
                  borderColor: isSelected ? accent.line : "#cbd5e1",
                }}
              >
                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
              </button>
            </div>
          )}
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[15px] font-bold text-slate-800 leading-tight">
                    {req.job_title}
                  </h3>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 border text-xs font-semibold ${jobStatusChipClass}`}
                  >
                    {jobStatus || "Active"}
                  </span>
                </div>
                {isAddressed ? (
                  <p className="mt-1 text-xs">
                    <span className="font-bold text-slate-900">
                      Addressed <span className="font-bold text-slate-600">By</span>{" "}
                    </span>
                    <span className="font-semibold text-slate-500">
                      {addressedByDisplay || "-"}
                    </span>
                  </p>
                ) : (
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    Not Addressed
                  </p>
                )}
              </div>

              {!selectionMode && (
                <div className="flex items-center gap-2 shrink-0">
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setCopiedDescription(false);
                        setShowDescriptionModal(true);
                      }}
                      disabled={!hasDescription}
                      className="px-3 py-1 rounded-xl text-[11px] font-bold border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      View Description
                    </button>
                    <button
                      onClick={() => onAddressed(req)}
                      className="px-3 py-1 rounded-xl text-[11px] font-bold text-white transition-all hover:opacity-90 active:scale-95"
                      style={{
                        background: currentUserAddressedFlag
                          ? "#dc2626"
                          : ADDRESSED_BUTTON_SOLID,
                        boxShadow: currentUserAddressedFlag
                          ? "0 4px 12px #dc262640"
                          : ADDRESSED_BUTTON_SHADOW,
                      }}
                    >
                      {currentUserAddressedFlag
                        ? "Revoke Address"
                        : "Addressed"}
                    </button>
                  </>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                  Client
                </p>
                <p className="text-sm font-semibold text-slate-800 break-words">
                  {req.client}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                  Job Code
                </p>
                <p className="text-sm font-semibold text-slate-800 break-words">
                  {req.job_code}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                  Location
                </p>
                <p className="text-sm font-semibold text-slate-800 break-words">
                  {req.location}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDescriptionModal && (
        <div
          className="fixed inset-0 z-[90] bg-slate-900/40 backdrop-blur-[1px] flex items-center justify-center p-4"
          onClick={() => setShowDescriptionModal(false)}
        >
          <div
            className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h4 className="text-sm font-bold text-slate-800 truncate">
                {req.job_title} - Description
              </h4>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyDescriptionToClipboard}
                  disabled={!hasDescription}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copiedDescription ? "Copied" : "Copy"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDescriptionModal(false)}
                  className="w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
                  aria-label="Close description popup"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-5 max-h-[70vh] overflow-auto">
              {hasDescription ? (
                <div
                  ref={descriptionContentRef}
                  className="text-sm text-slate-700 leading-6"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              ) : (
                <p className="text-sm text-slate-500">
                  No job description available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT — all original API logic preserved exactly
═══════════════════════════════════════════════════════════════════ */
const ActiveRequirements = ({ currentUser }) => {
  /* ── State (identical to original) ── */
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [filterWorkMode, setFilterWorkMode] = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterRemoteJob, setFilterRemoteJob] = useState("all");
  const [filterAddressedStatus, setFilterAddressedStatus] = useState("all");
  const [filterAddressedFname, setFilterAddressedFname] = useState("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [showAddressedModal, setShowAddressedModal] = useState(false);
  const [addressingValue, setAddressingValue] = useState("1");
  const [addressingNote, setAddressingNote] = useState("");
  const [addressingNoteError, setAddressingNoteError] = useState("");
  const [isSubmittingAddressed, setIsSubmittingAddressed] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPerPage, setServerPerPage] = useState(20);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});

  const ACTIVE_JOBS_PER_PAGE = 20;

  /* ── Helpers (identical to original) ── */
  const normalizeText = (value, fallback = "-") => {
    const text = String(value ?? "").trim();
    return text ? text : fallback;
  };
  const normalizeOptionalText = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return "";
    const lower = text.toLowerCase();
    if (lower === "null" || lower === "undefined") return "";
    return text;
  };

  const computeDaysOpen = (value) => {
    if (!value || value === "-") return 0;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 0;
    return Math.max(
      0,
      Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000)),
    );
  };

  const mapActiveJobToRequirement = (job) => {
    const location = normalizeText(
      job.location || [job.city, job.state].filter(Boolean).join(", "),
      "-",
    );
    const jobStartDate = normalizeText(
      job.job_start_date || job.created_at || job.api_created_at,
      "-",
    );
    const statusValue = normalizeText(job.job_status, "Active");
    const remoteJobValue = normalizeText(job.remote_job, "-");
    const rawAssignedTo = normalizeText(job.assigned_to, "-");
    const addressedFname = normalizeOptionalText(job.addressed_fname);
    const addressedLname = normalizeOptionalText(job.addressed_lname);
    const addressedUserName = [addressedFname, addressedLname]
      .filter(Boolean)
      .join(" ")
      .trim();
    const addressedByNames = normalizeOptionalText(
      job.addressed_by_names || job.addressed_user_names,
    );
    const addressedByNamesList = addressedByNames
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
    const currentUserAddressedFlagRaw = normalizeOptionalText(
      job.current_user_addressed_flag,
    ).toLowerCase();
    const currentUserAddressedFlag =
      currentUserAddressedFlagRaw === "1" ||
      currentUserAddressedFlagRaw === "true" ||
      currentUserAddressedFlagRaw === "yes"
        ? "1"
        : "0";
    const addressedStatus =
      addressedByNamesList.length > 0 ? "addressed" : "not_addressed";
    const assignedToCount =
      rawAssignedTo === "-"
        ? 0
        : rawAssignedTo.split(",").filter(Boolean).length;

    return {
      id: normalizeText(job.job_pk),
      job_pk: normalizeText(job.job_pk, ""),
      requirement_id: normalizeText(job.ceipal_ref, "-"),
      job_code: normalizeText(job.job_code, "-"),
      job_title: normalizeText(
        job.public_job_title || job.job_title,
        "Untitled Job",
      ),
      job_description_html: normalizeText(
        job.job_description_html || job.job_description,
        "",
      ),
      job_status: statusValue,
      job_type: normalizeText(job.job_type, "-"),
      client: normalizeText(job.client, "-"),
      end_client: normalizeText(job.end_client, "-"),
      account_manager: normalizeText(job.account_manager, "-"),
      assigned_to: rawAssignedTo,
      assigned_to_count: assignedToCount,
      addressed_status: addressedStatus,
      addressed_fname: addressedFname || "-",
      addressed_lname: addressedLname || "-",
      addressed_user_name: addressedUserName || "-",
      addressed_by_names: addressedByNames || "",
      current_user_addressed_flag: currentUserAddressedFlag,
      country: normalizeText(job.country, "-"),
      state: normalizeText(job.state, "-"),
      city: normalizeText(job.city, "-"),
      zip_code: normalizeText(job.zip_code, "-"),
      job_start_date: jobStartDate,
      job_end_date: normalizeText(job.job_end_date, "-"),
      duration: normalizeText(job.duration, "-"),
      required_hours_week: normalizeText(job.required_hours_week, "-"),
      notice_period: normalizeText(job.notice_period, "-"),
      remote_job: remoteJobValue,
      client_bill_rate_salary: normalizeText(
        job.client_bill_rate_salary || job.client_bill_rate___salary,
        "-",
      ),
      pay_rate_salary: normalizeText(
        job.pay_rate_salary || job.pay_rate___salary,
        "-",
      ),
      location,
      positions_count: Number(job.number_of_positions) || 0,
      submissions_count: 0,
      interviews_count: 0,
      created_at: normalizeText(
        job.created_at || job.api_created_at,
        new Date().toISOString(),
      ),
      updated_at: normalizeText(
        job.updated_at || job.api_modified_at,
        new Date().toISOString(),
      ),
      days_open: computeDaysOpen(jobStartDate),
      match_rate: 0,
    };
  };

  const currentUserId = String(
    currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId ?? "",
  ).trim();

  const getSelectionKey = (req) => String(req?.job_pk || req?.id || "").trim();

  /* ── Selection handlers (identical to original) ── */
  const toggleRequirementSelection = (req) => {
    const key = getSelectionKey(req);
    if (!key) return;
    setSelectedJobIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
    );
  };

  const toggleSelectAllCurrentPage = () => {
    const keys = paginatedRequirements
      .map((req) => getSelectionKey(req))
      .filter(Boolean);
    if (keys.length === 0) return;
    const allSelected = keys.every((key) => selectedJobIds.includes(key));
    setSelectedJobIds((prev) => {
      if (allSelected) return prev.filter((id) => !keys.includes(id));
      const merged = new Set(prev);
      keys.forEach((key) => merged.add(key));
      return Array.from(merged);
    });
  };

  const openAddressedModalForJobs = (jobIds, nextAddressingValue = "1") => {
    if (!Array.isArray(jobIds) || jobIds.length === 0) return;
    setSelectedJobIds(jobIds);
    setAddressingValue(nextAddressingValue === "0" ? "0" : "1");
    setAddressingNote("");
    setAddressingNoteError("");
    setShowAddressedModal(true);
  };

  const handleCardAddressed = (req) => {
    const key = getSelectionKey(req);
    if (!key) return;
    const nextAddressingValue =
      String(req?.current_user_addressed_flag || "0").trim() === "1"
        ? "0"
        : "1";
    openAddressedModalForJobs([key], nextAddressingValue);
  };

  const startSelectionMode = () => setSelectionMode(true);
  const cancelSelectionMode = () => {
    setSelectionMode(false);
    setSelectedJobIds([]);
  };

  /* ── API: submit addressed (identical to original) ── */
  const submitAddressedJobs = async () => {
    if (!currentUserId) {
      window.alert("Current user id is missing.");
      return;
    }
    if (selectedJobIds.length === 0) return;
    if (!String(addressingNote || "").trim()) {
      setAddressingNoteError("Notes are required.");
      return;
    }
    try {
      setIsSubmittingAddressed(true);
      setAddressingNoteError("");
      const payload = new URLSearchParams();
      const normalizedAddressingValue =
        String(addressingValue).trim() === "0" ? "0" : "1";
      payload.append("addressing_user_id", currentUserId);
      payload.append("current_user_id", currentUserId);
      payload.append("addressing_value", normalizedAddressingValue);
      payload.append("notes", addressingNote.trim());
      selectedJobIds.forEach((id) => payload.append("job_ids[]", id));

      const response = await fetch(
        "https://test.vdpl.co/api/index.php/setJobsAddressed",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json().catch(() => ({}));
      const success =
        result?.success === true ||
        result?.success === 1 ||
        result?.success === "1";
      if (!success)
        throw new Error(
          result?.error ||
            result?.message ||
            "Failed to update addressed jobs.",
        );

      setShowAddressedModal(false);
      setAddressingValue("1");
      setAddressingNote("");
      setSelectedJobIds([]);
      await fetchRequirements();
      await fetchJobStatusCounts();
    } catch (error) {
      console.error("Error setting jobs addressed:", error);
      window.alert(
        error?.message || "Failed to update selected jobs.",
      );
    } finally {
      setIsSubmittingAddressed(false);
    }
  };

  /* ── API: fetch requirements (identical to original) ── */
  const fetchRequirements = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", String(currentPage));
      params.append("per_page", String(ACTIVE_JOBS_PER_PAGE));
      params.append("current_user_id", currentUserId);
      if (searchTerm.trim()) params.append("q", searchTerm.trim());
      if (filterStatus !== "all") params.append("job_status", filterStatus);
      if (filterClient !== "all") params.append("client", filterClient);
      if (filterCountry !== "all") params.append("country", filterCountry);
      if (filterRemoteJob !== "all")
        params.append("remote_job", filterRemoteJob);
      if (filterWorkMode !== "all") params.append("job_type", filterWorkMode);
      if (filterAddressedStatus !== "all")
        params.append("is_addressed", filterAddressedStatus);
      if (filterAddressedFname !== "all")
        params.append("addressed_fname", filterAddressedFname);

      const response = await fetch(
        `https://test.vdpl.co/api/index.php/getActiveJobs?${params.toString()}`,
        { method: "GET" },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (!(result?.success === 1 || result?.success === true))
        throw new Error(result?.message || "Failed to fetch active jobs.");

      const rows = Array.isArray(result?.data) ? result.data : [];
      const mapped = rows.map(mapActiveJobToRequirement);
      setRequirements(mapped);

      const total = Number(result?.pagination?.total) || rows.length;
      const perPage =
        Number(result?.pagination?.per_page) || ACTIVE_JOBS_PER_PAGE;
      const totalPages =
        Number(result?.pagination?.total_pages) ||
        Math.max(1, Math.ceil(total / perPage));
      setServerTotal(total);
      setServerPerPage(perPage);
      setServerTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching requirements:", error);
      setRequirements([]);
      setServerTotal(0);
      setServerPerPage(ACTIVE_JOBS_PER_PAGE);
      setServerTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  /* ── API: fetch status counts (identical to original) ── */
  const fetchJobStatusCounts = async () => {
    try {
      const params = new URLSearchParams();
      params.append("current_user_id", currentUserId);
      const response = await fetch(
        `https://test.vdpl.co/api/index.php/get_job_status_counts?${params.toString()}`,
        { method: "GET" },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (!(result?.success === 1 || result?.success === true))
        throw new Error(
          result?.message || "Failed to fetch job status counts.",
        );
      setStatusCounts(
        result?.data && typeof result.data === "object" ? result.data : {},
      );
    } catch (error) {
      console.error("Error fetching job status counts:", error);
      setStatusCounts({});
    }
  };

  /* ── Effects (identical to original) ── */
  useEffect(() => {
    fetchRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchTerm,
    filterStatus,
    filterClient,
    filterCountry,
    filterRemoteJob,
    filterWorkMode,
    filterAddressedStatus,
    filterAddressedFname,
  ]);

  useEffect(() => {
    fetchJobStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const validIds = new Set(
      requirements.map((req) => getSelectionKey(req)).filter(Boolean),
    );
    setSelectedJobIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [requirements]);

  /* ── Derived (identical to original) ── */
  const totalPages = serverTotalPages;
  const paginatedRequirements = requirements;
  const pageStart =
    serverTotal === 0 ? 0 : (currentPage - 1) * serverPerPage + 1;
  const pageEnd =
    serverTotal === 0 ? 0 : Math.min(currentPage * serverPerPage, serverTotal);

  const currentPageSelectionKeys = paginatedRequirements
    .map((req) => getSelectionKey(req))
    .filter(Boolean);
  const isAllCurrentPageSelected =
    currentPageSelectionKeys.length > 0 &&
    currentPageSelectionKeys.every((key) => selectedJobIds.includes(key));
  const selectedRequirements = requirements.filter((req) =>
    selectedJobIds.includes(getSelectionKey(req)),
  );
  const selectedAddressingValue =
    selectedRequirements.length > 0 &&
    selectedRequirements.every(
      (req) => String(req.current_user_addressed_flag || "0").trim() === "1",
    )
      ? "0"
      : "1";

  const uniqueClients = [
    ...new Set(requirements.map((r) => r.client).filter((v) => v && v !== "-")),
  ];
  const uniqueWorkModes = [
    ...new Set(
      requirements.map((r) => r.job_type).filter((v) => v && v !== "-"),
    ),
  ];
  const uniqueStatuses = [
    ...new Set([
      ...DEFAULT_JOB_STATUSES,
      ...requirements
        .map((r) => r.job_status)
        .filter((v) => v && v !== "-"),
    ]),
  ];
  const uniqueCountries = [
    ...new Set(
      requirements.map((r) => r.country).filter((v) => v && v !== "-"),
    ),
  ];
  const uniqueRemoteJobs = [
    ...new Set(
      requirements.map((r) => r.remote_job).filter((v) => v && v !== "-"),
    ),
  ];
  const uniqueAddressedFnames = [
    ...new Set(
      requirements
        .map((r) => r.addressed_fname)
        .filter((v) => v && v !== "-" && v.toLowerCase() !== "null"),
    ),
  ];

  const kpiStatusCards = useMemo(() => {
    const statuses = ["Active", "Open", "Re-Open"];
    return statuses.map((status) => {
      const row = statusCounts?.[status] || {};
      const addressed = Number(row?.addressed) || 0;
      const notAddressed = Number(row?.not_addressed) || 0;
      return {
        status,
        total: addressed + notAddressed,
        addressed,
        notAddressed,
      };
    });
  }, [statusCounts]);

  const hasFilters =
    searchTerm ||
    filterStatus !== "all" ||
    filterClient !== "all" ||
    filterWorkMode !== "all" ||
    filterCountry !== "all" ||
    filterRemoteJob !== "all" ||
    filterAddressedStatus !== "all" ||
    filterAddressedFname !== "all";

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterClient("all");
    setFilterWorkMode("all");
    setFilterCountry("all");
    setFilterRemoteJob("all");
    setFilterAddressedStatus("all");
    setFilterAddressedFname("all");
    setCurrentPage(1);
  };

  const isRevokeAddressAction = addressingValue === "0";

  /* ══════════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
        *, *::before, *::after { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; }
        .mono { font-family: 'JetBrains Mono', monospace !important; }

        @keyframes kpiSlide   { from{opacity:0;transform:translateY(22px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes cardFadeIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
        @keyframes headerIn   { from{opacity:0;transform:translateY(-18px)} to{opacity:1;transform:translateY(0)} }
        @keyframes filterIn   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes floatBob   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes shimPulse  { 0%,100%{opacity:0.45} 50%{opacity:0.9} }
        @keyframes modalIn    { from{opacity:0;transform:scale(0.94) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes successPop { 0%,100%{transform:scale(1)} 50%{transform:scale(1.07)} }
        @keyframes rainbowLine{ 0%{background-position:0% 50%} 100%{background-position:200% 50%} }

        select { -webkit-appearance:none; appearance:none; }
        select option { background:#fff; color:#0f172a; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-track { background:#f1f5f9; }
        ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
      `}</style>

      <div
        className="min-h-screen"
        style={{
          background:
            "linear-gradient(160deg,#f0f4ff 0%,#f8faff 40%,#fafaf8 100%)",
        }}
      >
        {/* Top mesh glow */}
        <div
          className="fixed top-0 left-0 right-0 h-80 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% -15%,#dbeafe70,transparent)",
            zIndex: 0,
          }}
        />

        <div className="relative z-10 max-w-[1500px] mx-auto px-4 sm:px-5 py-5 space-y-4">
          {/* ══ HEADER ══ */}
          <div
            className="rounded-3xl overflow-hidden"
            style={{
              animation: "headerIn 0.5s ease both",
              background: "#ffffff",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 4px 24px -6px #00000012, 0 1px 4px -1px #00000008",
            }}
          >
            {/* Animated rainbow stripe */}
            <div
              style={{
                height: 4,
                background:
                  "linear-gradient(90deg,#3b82f6,#8b5cf6,#ec4899,#f59e0b,#10b981,#3b82f6)",
                backgroundSize: "300% 100%",
                animation: "rainbowLine 6s linear infinite",
              }}
            />

            <div className="p-5 pb-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-4">
                <div className="flex items-center gap-4 flex-1">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                    style={{
                      background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                      boxShadow: "0 8px 24px #3b82f650",
                      animation: "floatBob 4s ease-in-out infinite",
                    }}
                  >
                    <Briefcase className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5">
                      <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                        Active Requirements
                      </h1>
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-[10px] font-black px-2.5 py-1 rounded-full border border-blue-200">
                        <Sparkles className="w-2.5 h-2.5" />
                        LIVE
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm mt-0.5 font-medium">
                      Manage requisitions · Match candidates · Track progress
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 shadow-sm">
                    <div className="w-6 h-6 rounded-lg bg-blue-500 flex items-center justify-center shadow-sm">
                      <TrendingUp className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-black text-slate-800">
                      {serverTotal}
                    </span>
                    <span className="text-xs text-slate-500 font-medium">
                      Total
                    </span>
                  </div>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 justify-items-center max-w-5xl mx-auto">
                {kpiStatusCards.map((d, i) => (
                  <KpiCard key={d.status} {...d} delay={i * 120} />
                ))}
              </div>
            </div>
          </div>

          {/* ══ FILTERS ══ */}
          <div
            className="rounded-2xl overflow-hidden bg-white"
            style={{
              animation: "filterIn 0.5s ease 0.18s both",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 2px 16px -4px #00000010",
            }}
          >
            <div className="p-4">
              <div className="flex flex-wrap xl:flex-nowrap items-center gap-2.5">
                {/* Search */}
                <div className="relative flex-1 min-w-[300px]">
                  <div
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
                    style={{
                      background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                    }}
                  >
                    <Search className="w-3.5 h-3.5 text-white" />
                  </div>
                  <input
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search requirements…"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-14 pr-10 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all font-medium"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setCurrentPage(1);
                      }}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Filter Row */}
                <div className="flex flex-wrap items-center gap-2">
                {/* Status */}
                <div className="relative flex items-center">
                  <div
                    className="absolute left-3 w-5 h-5 rounded-md flex items-center justify-center pointer-events-none shadow-sm"
                    style={{
                      background: "linear-gradient(135deg,#475569,#64748b)",
                    }}
                  >
                    <FileText className="w-2.5 h-2.5 text-white" />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="appearance-none bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-8 py-2.5 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    {uniqueStatuses.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>

                {/* Job Type */}
                <div className="relative flex items-center">
                  <div
                    className="absolute left-3 w-5 h-5 rounded-md flex items-center justify-center pointer-events-none shadow-sm"
                    style={{
                      background: "linear-gradient(135deg,#4338ca,#6366f1)",
                    }}
                  >
                    <Briefcase className="w-2.5 h-2.5 text-white" />
                  </div>
                  <select
                    value={filterWorkMode}
                    onChange={(e) => {
                      setFilterWorkMode(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="appearance-none bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-8 py-2.5 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All Job Types</option>
                    {uniqueWorkModes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>

                {/* Addressed Status */}
                <div className="relative flex items-center">
                  <div
                    className="absolute left-3 w-5 h-5 rounded-md flex items-center justify-center pointer-events-none shadow-sm"
                    style={{
                      background: "linear-gradient(135deg,#047857,#10b981)",
                    }}
                  >
                    <CheckCircle className="w-2.5 h-2.5 text-white" />
                  </div>
                  <select
                    value={filterAddressedStatus}
                    onChange={(e) => {
                      setFilterAddressedStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="appearance-none bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-8 py-2.5 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All</option>
                    <option value="1">Addressed</option>
                    <option value="0">Not Addressed</option>
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>

                {/* More filters */}
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all
                    ${showAdvancedFilters ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white"}`}
                >
                  <Filter
                    className={`w-3.5 h-3.5 ${showAdvancedFilters ? "text-indigo-500" : "text-slate-400"}`}
                  />
                  More Filters
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${showAdvancedFilters ? "rotate-180" : ""}`}
                  />
                </button>

                {hasFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-bold hover:bg-red-100 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear All
                  </button>
                )}
                </div>
              </div>

              {/* Advanced filters panel */}
              {showAdvancedFilters && (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {/* Client */}
                    <div>
                      <label className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-wider mb-2">
                        <div
                          className="w-4 h-4 rounded flex items-center justify-center shadow-sm"
                          style={{
                            background:
                              "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                          }}
                        >
                          <Building2 className="w-2.5 h-2.5 text-white" />
                        </div>
                        Client Company
                      </label>
                      <div className="relative">
                        <select
                          value={filterClient}
                          onChange={(e) => {
                            setFilterClient(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full appearance-none bg-white border border-indigo-200 rounded-xl px-4 py-2.5 pr-8 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
                        >
                          <option value="all">All Clients</option>
                          {uniqueClients.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    {/* Country */}
                    <div>
                      <label className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-wider mb-2">
                        <div
                          className="w-4 h-4 rounded flex items-center justify-center shadow-sm"
                          style={{
                            background:
                              "linear-gradient(135deg,#047857,#10b981)",
                          }}
                        >
                          <MapPin className="w-2.5 h-2.5 text-white" />
                        </div>
                        Country
                      </label>
                      <div className="relative">
                        <select
                          value={filterCountry}
                          onChange={(e) => {
                            setFilterCountry(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full appearance-none bg-white border border-indigo-200 rounded-xl px-4 py-2.5 pr-8 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
                        >
                          <option value="all">All Countries</option>
                          {uniqueCountries.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    {/* Remote */}
                    <div>
                      <label className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-wider mb-2">
                        <div
                          className="w-4 h-4 rounded flex items-center justify-center shadow-sm"
                          style={{
                            background:
                              "linear-gradient(135deg,#6d28d9,#8b5cf6)",
                          }}
                        >
                          <Wifi className="w-2.5 h-2.5 text-white" />
                        </div>
                        Remote Job
                      </label>
                      <div className="relative">
                        <select
                          value={filterRemoteJob}
                          onChange={(e) => {
                            setFilterRemoteJob(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full appearance-none bg-white border border-indigo-200 rounded-xl px-4 py-2.5 pr-8 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
                        >
                          <option value="all">All Remote Types</option>
                          {uniqueRemoteJobs.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    {/* Addressed User */}
                    <div>
                      <label className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-wider mb-2">
                        <div
                          className="w-4 h-4 rounded flex items-center justify-center shadow-sm"
                          style={{
                            background:
                              "linear-gradient(135deg,#dc2626,#f97316)",
                          }}
                        >
                          <CheckCircle className="w-2.5 h-2.5 text-white" />
                        </div>
                        Addressed User
                      </label>
                      <div className="relative">
                        <select
                          value={filterAddressedFname}
                          onChange={(e) => {
                            setFilterAddressedFname(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full appearance-none bg-white border border-indigo-200 rounded-xl px-4 py-2.5 pr-8 text-sm text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
                        >
                          <option value="all">All Addressed Users</option>
                          {uniqueAddressedFnames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Results bar */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/60">
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center shadow-sm"
                  style={{
                    background: "linear-gradient(135deg,#0369a1,#0ea5e9)",
                  }}
                >
                  <Eye className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-slate-500 font-medium">
                  Showing{" "}
                  <span className="text-slate-800 font-black">
                    {paginatedRequirements.length}
                  </span>{" "}
                  of{" "}
                  <span className="text-slate-800 font-black">
                    {serverTotal}
                  </span>{" "}
                  requirements
                </span>
              </div>

              <div className="flex items-center gap-2">
                {!selectionMode ? (
                  <button
                    onClick={startSelectionMode}
                    disabled={loading || paginatedRequirements.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-40 shadow-sm"
                  >
                    <CheckCircle className="w-3 h-3 text-slate-400" />
                    Select
                  </button>
                ) : (
                  <>
                    <button
                      onClick={toggleSelectAllCurrentPage}
                      disabled={loading || paginatedRequirements.length === 0}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 shadow-sm transition-all disabled:opacity-40"
                    >
                      {isAllCurrentPageSelected
                        ? "Clear Selection"
                        : "Select All"}
                    </button>
                    <button
                      onClick={() =>
                        openAddressedModalForJobs(
                          selectedJobIds,
                          selectedAddressingValue,
                        )
                      }
                      disabled={selectedJobIds.length === 0 || !currentUserId}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-black text-white shadow-sm transition-all disabled:opacity-40"
                      style={{
                        background:
                          selectedAddressingValue === "0"
                            ? "linear-gradient(135deg,#b91c1c,#ef4444)"
                            : ADDRESSED_BUTTON_GRADIENT,
                      }}
                    >
                      <Award className="w-3 h-3" />
                      {selectedAddressingValue === "0"
                        ? "Revoke Address"
                        : "Addressed"}
                      {selectedJobIds.length > 0 &&
                        ` (${selectedJobIds.length})`}
                    </button>
                    <button
                      onClick={cancelSelectionMode}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-500 border border-red-200 bg-red-50 hover:bg-red-100 transition-all"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ══ CARD LIST ══ */}
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="max-w-6xl mx-auto rounded-2xl border border-slate-200 bg-white h-32"
                  style={{
                    animation: `shimPulse 1.4s ease ${i * 200}ms infinite`,
                  }}
                />
              ))}
            </div>
          ) : paginatedRequirements.length === 0 ? (
            <div className="max-w-6xl mx-auto rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
              <div style={{ animation: "floatBob 3s ease-in-out infinite" }}>
                <Briefcase className="w-14 h-14 mx-auto mb-4 text-slate-300" />
              </div>
              <p className="text-slate-700 font-black text-lg mb-1">
                No Requirements Found
              </p>
              <p className="text-slate-400 text-sm font-medium">
                {hasFilters
                  ? "Try adjusting your filters"
                  : "Get started by adding your first requirement"}
              </p>
              {hasFilters && (
                <button
                  onClick={clearAllFilters}
                  className="mt-4 px-5 py-2 rounded-xl text-sm font-bold text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedRequirements.map((req, i) => (
                <JobCard
                  key={req.id}
                  req={req}
                  selectionMode={selectionMode}
                  isSelected={selectedJobIds.includes(getSelectionKey(req))}
                  onToggle={toggleRequirementSelection}
                  onAddressed={handleCardAddressed}
                  delay={i * 55}
                />
              ))}
            </div>
          )}

          {/* ══ PAGINATION ══ */}
          {totalPages > 1 && (
            <div
              className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              style={{ animation: "filterIn 0.4s ease 0.3s both" }}
            >
              <span className="text-sm text-slate-500 font-medium">
                Showing{" "}
                <span className="font-black text-slate-800">{pageStart}</span>{" "}
                to <span className="font-black text-slate-800">{pageEnd}</span>{" "}
                of{" "}
                <span className="font-black text-slate-800">{serverTotal}</span>{" "}
                requirements
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-slate-500 border border-slate-200 bg-white hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 transition-all shadow-sm"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-xl text-slate-500 border border-slate-200 bg-white hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 transition-all shadow-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum =
                      currentPage <= 3
                        ? i + 1
                        : currentPage >= totalPages - 2
                          ? totalPages - 4 + i
                          : currentPage - 2 + i;
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-9 h-9 rounded-xl text-xs font-black transition-all shadow-sm"
                        style={
                          pageNum === currentPage
                            ? {
                                background:
                                  "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                                color: "#fff",
                                boxShadow: "0 4px 14px #3b82f650",
                              }
                            : {
                                background: "#fff",
                                color: "#475569",
                                border: "1.5px solid #e2e8f0",
                              }
                        }
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-xl text-slate-500 border border-slate-200 bg-white hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 transition-all shadow-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-slate-500 border border-slate-200 bg-white hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 transition-all shadow-sm"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
        {/* /container */}

        {/* ══ ADDRESSED MODAL ══ */}
        {showAddressedModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              background: "rgba(15,23,42,0.45)",
              backdropFilter: "blur(6px)",
            }}
          >
            <div
              className="w-full max-w-lg rounded-2xl overflow-hidden bg-white"
              style={{
                animation: "modalIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both",
                border: "1.5px solid #e2e8f0",
                boxShadow: "0 24px 64px -12px #00000028",
              }}
            >
              <div
                style={{
                  height: 4,
                  background: isRevokeAddressAction
                    ? "linear-gradient(90deg,#b91c1c,#ef4444,#f97316)"
                    : "linear-gradient(90deg,#1d4ed8,#3b82f6,#8b5cf6)",
                }}
              />

              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                    style={{
                      background: isRevokeAddressAction
                        ? "linear-gradient(135deg,#b91c1c,#ef4444)"
                        : "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                    }}
                  >
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-slate-800">
                      {isRevokeAddressAction
                        ? "Revoke Address"
                        : "Mark Requirements as Addressed"}
                    </h3>
                    <p className="text-xs text-slate-400 font-medium mt-0.5">
                      {selectedJobIds.length} requirement
                      {selectedJobIds.length !== 1 ? "s" : ""} selected
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (isSubmittingAddressed) return;
                    setShowAddressedModal(false);
                    setAddressingValue("1");
                    setAddressingNote("");
                    setAddressingNoteError("");
                  }}
                  className="w-8 h-8 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5">
                <div
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 ${
                    isRevokeAddressAction
                      ? "bg-red-50 border border-red-200"
                      : "bg-blue-50 border border-blue-200"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center shadow-sm shrink-0 ${
                      isRevokeAddressAction ? "bg-red-500" : "bg-blue-500"
                    }`}
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span
                    className={`text-sm font-semibold ${
                      isRevokeAddressAction ? "text-red-700" : "text-blue-700"
                    }`}
                  >
                    {selectedJobIds.length} job
                    {selectedJobIds.length !== 1 ? "s" : ""} will be{" "}
                    {isRevokeAddressAction ? "revoked" : "marked as addressed"}
                  </span>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Note <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={addressingNote}
                    onChange={(e) => {
                      setAddressingNote(e.target.value);
                      if (addressingNoteError) setAddressingNoteError("");
                    }}
                    rows={3}
                    placeholder={
                      isRevokeAddressAction
                        ? "Add revoke note..."
                        : "Add addressed note..."
                    }
                    className={`w-full rounded-xl bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all ${
                      addressingNoteError
                        ? "border border-red-300 focus:ring-red-200 focus:border-red-400"
                        : "border border-slate-200"
                    }`}
                  />
                  {addressingNoteError && (
                    <p className="mt-2 text-xs font-semibold text-red-600">
                      {addressingNoteError}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50">
                <button
                  onClick={() => {
                    setShowAddressedModal(false);
                    setAddressingValue("1");
                    setAddressingNote("");
                    setAddressingNoteError("");
                  }}
                  disabled={isSubmittingAddressed}
                  className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 transition-all shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={submitAddressedJobs}
                  disabled={
                    isSubmittingAddressed ||
                    selectedJobIds.length === 0 ||
                    !currentUserId
                  }
                  className="flex items-center gap-2 px-6 py-2 rounded-xl text-sm font-black text-white transition-all disabled:opacity-60 shadow-md"
                  style={{
                    background: isRevokeAddressAction
                      ? "linear-gradient(135deg,#b91c1c,#ef4444)"
                      : ADDRESSED_BUTTON_GRADIENT,
                    boxShadow: isRevokeAddressAction
                      ? "0 4px 14px #ef444450"
                      : ADDRESSED_BUTTON_SHADOW,
                  }}
                >
                  {isSubmittingAddressed ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      <Award className="w-3.5 h-3.5" />
                      {isRevokeAddressAction ? "Revoke Address" : "Addressed"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ActiveRequirements;
